import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import type { DatesSetArg, EventContentArg } from '@fullcalendar/core';
import ptBr from '@fullcalendar/core/locales/pt-br';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import timeGridPlugin from '@fullcalendar/timegrid';
import FullCalendar from '@fullcalendar/react';

import './calendar.css';
import {
  dayOf,
  describeItem,
  endOf,
  localDayOf,
  nowInChurchTime,
  toEventInput,
} from '../lib/adapters';
import { chipClassOf, dotClassOf } from '../lib/appearance';
import type { CalendarView } from '../lib/viewState';
import { addDaysToDateInput, toDateInputValue } from '@/shared/lib/dates';
import { cn } from '@/shared/lib/utils';
import type { CalendarItem } from '@/shared/types/domain';

interface ProjectCalendarProps {
  items: readonly CalendarItem[];
  view: CalendarView;
  /** `yyyy-mm-dd` — sempre há um dia selecionado (decisão 72). */
  selectedDay: string;
  onSelectDay: (day: string) => void;
  onOpenItem: (item: CalendarItem) => void;
  /** Botão direito sobre um dia (decisão 75). `null` para quem não tem `writeDraft`. */
  onDayContextMenu: ((day: string, position: { x: number; y: number }) => void) | null;
  /**
   * `datesSet`: o período mudou — a página reancora a consulta e o título.
   *
   * ⚠️ **Quatro campos, e a distinção entre os dois pares é o que corrige o
   * título.** `anchor`/`periodEnd` são o PERÍODO (agosto inteiro);
   * `start`/`end`, a GRADE visível (27 de julho a 6 de setembro, porque o mês
   * desenha as semanas inteiras das pontas). Rotular a tela com o par errado
   * produzia *"Julho – setembro 2026"* em cima de um mês de agosto.
   */
  onPeriodChange: (info: {
    anchor: string;
    /** Primeiro dia DEPOIS do período — exclusivo, como o FullCalendar entrega. */
    periodEnd: string;
    start: string;
    end: string;
  }) => void;
  /** Abaixo de `sm` a célula troca chips por marcadores (decisão 59). */
  isNarrow: boolean;
  /**
   * A grade recebe a altura do PAINEL (`lg+`) ou cresce com o conteúdo.
   *
   * ⚠️ **É prop, e não CSS, porque `height` do FullCalendar é decisão dele.**
   * A primeira tentativa foi `height="100%"` fixo com um `height: auto
   * !important` no `calendar.css` abaixo de `lg` — e em 375px a grade
   * **colapsou**: sobrou o cabeçalho "DOM SEG TER…" e nenhuma semana, porque o
   * 100% do componente não tem pai de altura definida quando a página rola. O
   * FullCalendar precisa saber a regra, não descobri-la por cascata.
   */
  fillHeight: boolean;
  /** A âncora inicial, para a grade voltar onde a pessoa estava (§8). */
  initialDay: string;
}

/**
 * A grade — **o único arquivo do painel que importa o FullCalendar**
 * (spec-calendar.md §10).
 *
 * ⚠️ **`timeZone: 'local'`, e não `TIME_ZONE`** — a armadilha desta feature
 * (decisão 63). O FullCalendar aceita nativamente só `'local'` e `'UTC'`; fuso
 * nomeado exige plugin de timezone, que seriam duas bibliotecas para fazer o que
 * o `Intl` já faz em `shared/lib/dates.ts`. A saída é o adaptador entregar
 * **hora de parede de São Paulo sem offset** e o FullCalendar renderizar a
 * string como a recebeu. `now` fecha o único vazamento que sobra: sem ele,
 * "hoje" sairia do relógio do navegador e um pastor viajando veria o dia
 * marcado no dia errado.
 *
 * **Nada aqui é escrivível**, e as quatro proibições são explícitas em vez de
 * confiadas ao padrão: arrastar um evento é o gesto mais natural do mundo, e
 * aqui ele teria de virar transição de esteira — que só existe por RPC e exige
 * comentário em `reject`/`request_changes` (regra 2 do CLAUDE.md). O único ato
 * de escrita que a tela oferece é um atalho para COMEÇAR um projeto, em outra
 * tela.
 *
 * ── CÉLULA DE TAMANHO FIXO (spec-calendar-ux.md §6.2, decisão 94) ────────────
 *
 * **Toda célula do mês tem exatamente a mesma altura, com 0 ou com 8 itens.** O
 * mecanismo não é `min-height`: é `dayMaxEvents={true}` — *"corte pelo que
 * couber"* —, com a grade recebendo altura do PAINEL (`height="100%"`) e
 * `expandRows` distribuindo as semanas por igual. Sem isso, o sábado de dezembro
 * com oito projetos estica a linha daquela semana e o mês deixa de ser uma
 * malha: o olho perde a referência de "uma semana = uma faixa".
 *
 * O "e mais N" **seleciona o dia** em vez de abrir um balão — e é por isso que a
 * lista da esquerda está sempre visível: ela é onde os oito cabem.
 */
export interface CalendarControls {
  prev(): void;
  next(): void;
  today(): void;
}

export const ProjectCalendar = forwardRef<CalendarControls, ProjectCalendarProps>(
  function ProjectCalendar(
    {
      items,
      view,
      selectedDay,
      onSelectDay,
      onOpenItem,
      onDayContextMenu,
      onPeriodChange,
      isNarrow,
      fillHeight,
      initialDay,
    },
    controlsRef,
  ) {
    const ref = useRef<FullCalendar>(null);
    const wrapperRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
      controlsRef,
      () => ({
        prev: () => ref.current?.getApi().prev(),
        next: () => ref.current?.getApi().next(),
        today: () => ref.current?.getApi().today(),
      }),
      [],
    );

    /**
     * Trocar de vista pelo seletor: o FullCalendar é imperativo, e mudar a prop
     * `initialView` não o move. Este efeito é a ponte entre o estado de React e
     * a API dele — e é o único lugar que a atravessa.
     *
     * **A vista nova abre no DIA SELECIONADO**, e não em hoje. Sem isso, quem
     * escolhia 28 de agosto e clicava em "Semana" caía na semana corrente, a
     * seleção era empurrada para o primeiro dia visível e a lista ao lado
     * passava a falar de outro dia — a tela desfazendo a escolha que a pessoa
     * acabou de fazer.
     *
     * O dia entra por `ref` e não por dependência: como dependência, cada clique
     * num dia renavegaria a grade e a vista de mês pularia de página ao
     * selecionar um dia da semana vizinha.
     */
    const selectedRef = useRef(selectedDay);
    selectedRef.current = selectedDay;

    useEffect(() => {
      ref.current?.getApi().changeView(view, selectedRef.current);
    }, [view]);

    /**
     * Em 375px a grade do MÊS não recebe evento nenhum: a célula vira
     * marcadores (abaixo), e deixar o FullCalendar posicionar chips de 40px de
     * largura só produziria "+3" em toda casa do mês.
     *
     * ⚠️ **`view === 'dayGridMonth'` na condição, e não só `isNarrow`.** A
     * troca vale para a célula do mês, que é a que tem 40px; a Semana em 375px
     * tem sete colunas de altura inteira e nenhum marcador para cair — sem a
     * ressalva ela ficava literalmente VAZIA no telefone, com a lista ao lado
     * listando os projetos que a grade jurava não existir.
     */
    const events = useMemo(
      () => (isNarrow && view === 'dayGridMonth' ? [] : items.map(toEventInput)),
      [isNarrow, items, view],
    );

    /**
     * Um por dia: quantos itens há, e de que matizes. Só usado em 375px.
     *
     * ⚠️ Antes eram marcadores por FONTE; agora são por SITUAÇÃO, pela mesma
     * troca de eixo do chip (§4). No máximo quatro, que é o teto de matizes.
     */
    const markers = useMemo(() => {
      if (!isNarrow || view !== 'dayGridMonth') {
        return new Map<string, { dots: string[]; count: number }>();
      }

      const byDay = new Map<string, { dots: Set<string>; count: number }>();
      for (const item of items) {
        // O item de vários dias marca todos os dias que atravessa, como na lista.
        for (const day of daysCovered(item)) {
          const entry = byDay.get(day) ?? { dots: new Set<string>(), count: 0 };
          entry.dots.add(dotClassOf(item));
          entry.count += 1;
          byDay.set(day, entry);
        }
      }

      return new Map(
        [...byDay].map(([day, entry]) => [day, { dots: [...entry.dots], count: entry.count }]),
      );
    }, [isNarrow, items, view]);

    const handleDatesSet = useCallback(
      (arg: DatesSetArg) => {
        onPeriodChange({
          // `currentStart`/`currentEnd` são o PERÍODO; `start`/`end`, a grade.
          anchor: localDayOf(arg.view.currentStart),
          periodEnd: localDayOf(arg.view.currentEnd),
          start: localDayOf(arg.start),
          end: localDayOf(arg.end),
        });
      },
      [onPeriodChange],
    );

    /**
     * Botão direito, por delegação num só listener.
     *
     * Um listener por célula (via `dayCellDidMount`) seria o caminho óbvio e
     * criaria 42 registros por mês, refeitos a cada navegação. A célula já
     * carrega `data-date`, que é tudo de que o menu precisa.
     */
    useEffect(() => {
      const node = wrapperRef.current;
      if (!node || !onDayContextMenu) return;

      function handle(event: MouseEvent) {
        const cell = (event.target as HTMLElement).closest<HTMLElement>('[data-date]');
        const day = cell?.dataset.date;
        if (!day) return;

        event.preventDefault();
        onDayContextMenu?.(day.slice(0, 10), { x: event.clientX, y: event.clientY });
      }

      node.addEventListener('contextmenu', handle);
      return () => node.removeEventListener('contextmenu', handle);
    }, [onDayContextMenu]);

    return (
      // `min-h-0` é o par obrigatório do `flex-1`: sem ele o filho de altura
      // 100% empurraria o flex para além do painel e a página voltaria a rolar,
      // desfazendo a célula fixa (§6.2).
      <div ref={wrapperRef} className="ibft-calendar lg:flex lg:min-h-0 lg:flex-1 lg:flex-col">
        <FullCalendar
          ref={ref}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={view}
          initialDate={initialDay}
          locale={ptBr}
          // ⚠️ Ver o docblock: hora de parede já convertida pelo adaptador.
          timeZone="local"
          now={nowInChurchTime()}
          headerToolbar={false}
          // 100% em cima do painel de altura definida (`AppShell.fullHeight`);
          // `auto` abaixo de `lg`, onde a página rola e não há altura de quem
          // herdar (ver o docblock da prop).
          height={fillHeight ? '100%' : 'auto'}
          events={events}
          firstDay={0}
          fixedWeekCount={false}
          /**
           * ⚠️ `true`, não um número — é ELE que faz a célula ter tamanho fixo
           * (decisão 94). Com `dayMaxEvents={3}` a célula ainda crescia até
           * caber três; com `true`, o FullCalendar corta pelo que couber na
           * altura que a linha REALMENTE tem, e a altura vem do painel.
           */
          dayMaxEvents
          /**
           * ── A Semana, e só ela (por ora) ────────────────────────────────
           *
           * **`slotEventOverlap: false` é o que faz os simultâneos se
           * espremerem lado a lado** em vez de se empilharem em escada. O
           * padrão do FullCalendar é `true`: o segundo evento entra recuado e
           * POR CIMA do primeiro, cobrindo o título dele — três reuniões
           * sobrepostas viravam três títulos ilegíveis, e o conflito de espaço
           * (o alerta que a US-20 existe para dar) ficava invisível justamente
           * na tela que deveria mostrá-lo.
           *
           * **`dayMaxEvents: 2` na faixa de dia inteiro**, com número em vez de
           * `true`: `true` mede a altura da linha, e a faixa da Semana tem
           * altura automática — a conta dá zero e some com a barra.
           */
          views={{
            timeGridWeek: { slotEventOverlap: false, dayMaxEvents: 2 },
          }}
          moreLinkContent={(arg) => `e mais ${arg.num}`}
          // Clicar em "+2" seleciona o dia em vez de abrir um balão: a lista ao
          // lado já é o lugar onde o dia inteiro cabe, e um popover sobre a
          // grade esconderia justamente o mês que a pessoa estava lendo.
          moreLinkClick={(arg) => {
            onSelectDay(localDayOf(arg.date));
            return 'none';
          }}
          // Nada é escrivível — explícito, e não confiado ao padrão.
          editable={false}
          selectable={false}
          eventStartEditable={false}
          eventDurationEditable={false}
          droppable={false}
          dateClick={(arg) => onSelectDay(arg.dateStr.slice(0, 10))}
          eventClick={(arg) => {
            const item = arg.event.extendedProps.item as CalendarItem | undefined;
            if (item) onOpenItem(item);
          }}
          datesSet={handleDatesSet}
          dayCellClassNames={(arg) =>
            localDayOf(arg.date) === selectedDay ? ['ibft-day-selected'] : []
          }
          /**
           * ⚠️ **Sempre nosso, mesmo no desktop** — e a razão é um defeito que
           * custou uma rodada para ser visto: o disco de "hoje" é desenhado pelo
           * CSS em `.fc-daygrid-day-number`, e essa caixa passa a EMBRULHAR o
           * conteúdo que entregamos aqui. Com o número e os marcadores dentro
           * dela, o disco tentava conter os dois e virava um borrão azul por
           * cima do dia 18. O número ganhou classe própria (`ibft-day-number`),
           * o disco foi para ela, e por isso ela precisa existir nas duas
           * larguras.
           */
          dayCellContent={(arg) => (
            <DayCell
              dayNumber={arg.dayNumberText}
              marker={isNarrow ? markers.get(localDayOf(arg.date)) : undefined}
            />
          )}
          eventContent={(arg) =>
            isTimeBlock(arg) ? <TimeBlock arg={arg} /> : <Chip arg={arg} />
          }
          // A faixa em que a igreja funciona. A grade de 24 horas em 375px é
          // ilegível, e esconder a vista seria pior do que apertá-la.
          slotMinTime="06:00:00"
          slotMaxTime="23:59:00"
          slotDuration="01:00:00"
          /**
           * A faixa de "dia inteiro", como no Calendário do macOS — e ela não é
           * enfeite: sem ela, um projeto de vários dias é espremido dentro da
           * grade de horas e reaparece em TODAS as colunas da semana às 06:00,
           * fingindo ser sete compromissos matinais. Na faixa ele é uma barra
           * contínua, que é o que ele é.
           */
          allDaySlot
          allDayText="dia inteiro"
          nowIndicator
          expandRows
        />
      </div>
    );
  },
);

/**
 * O chip: **filete · título · hora** (spec-calendar-ux.md §4.3).
 *
 * Uma linha, e nada mais. Ministério, espaço e situação chegam pelo `title` do
 * elemento (mouse parado, leitor de tela), pela folha e pela lista do dia — a
 * US-12 pede que a TELA mostre esses campos, e a tela mostra; a célula de 2mm
 * não é onde eles cabem.
 *
 * ⚠️ **Sumiram duas coisas daqui, e as duas por terem virado a COR:** o marcador
 * de fonte (não há fontes) e o ponto de situação. Hoje o chip carrega dois
 * canais — matiz (situação) e preenchimento (acesso) —, e um terceiro marcador
 * dentro dele seria a mesma informação dita duas vezes.
 */
function Chip({ arg }: { arg: EventContentArg }) {
  const item = arg.event.extendedProps.item as CalendarItem | undefined;
  if (!item) return null;

  const start = arg.event.start;
  /**
   * **Barra de vários dias não mostra hora**, e a omissão é leitura, não
   * economia de pixel: o FullCalendar quebra a barra em um segmento por semana,
   * e cada segmento repetiria o mesmo "08:00" a 600px de distância do título —
   * o olho lê aquilo como um item solto no sábado. A janela inteira está na
   * lista do dia, ao lado, onde ela cabe por extenso.
   *
   * `allDay` é a mesma condição, decidida no adaptador (`spansDays`).
   */
  const multiDay = arg.event.allDay;
  const isFull = item.access === 'full';

  return (
    <div
      title={describeItem(item)}
      className={cn(
        'flex w-full items-center gap-1.5 overflow-hidden rounded-[4px] border-l-2 px-1.5 py-[1px]',
        'transition-[filter,background-color] duration-fast ease-out hover:brightness-[0.97]',
        chipClassOf(item),
      )}
    >
      <span
        className={cn(
          'min-w-0 flex-1 truncate text-[0.6875rem]',
          // O peso e a cor do título são o segundo sinal do preenchimento: o que
          // abre inteiro tem presença de conteúdo; o que só se consulta recua
          // para o tom de apoio, como uma legenda.
          isFull ? 'font-medium text-foreground' : 'font-normal text-muted-foreground',
        )}
      >
        {arg.event.title}
      </span>
      {start && !multiDay ? (
        <span className="tabular shrink-0 text-[0.625rem] text-muted-foreground">
          {formatTimeOfLocal(start)}
        </span>
      ) : null}
    </div>
  );
}

/**
 * O evento é desenhado como BLOCO de horas, e não como chip de célula.
 *
 * Hoje só a Semana — a vista de Dia continua no chip até a rodada dela. Trocar
 * o `===` por `startsWith('timeGrid')` é a única linha que a inclui.
 *
 * `allDay` fica de fora de propósito: na faixa de dia inteiro o item é uma
 * barra de uma linha, que é exatamente o chip.
 */
function isTimeBlock(arg: EventContentArg): boolean {
  return arg.view.type === 'timeGridWeek' && !arg.event.allDay;
}

/**
 * O bloco da vista de Semana: **filete · título · janela**, ocupando a altura
 * da duração (spec-calendar-ux.md §4.3, estendida).
 *
 * ⚠️ **Por que não é o `Chip`.** O chip é uma LINHA — a anatomia certa para a
 * célula do mês, que não tem eixo vertical. Dentro do arnês de horas ele se
 * encolhe no topo: um projeto das 09:00 às 11:00 vira um traço de 20px sobre
 * 50px de vazio, e a grade passa a informar só o começo. A vista de horas existe
 * para responder *quanto tempo isso ocupa o espaço* — sem a altura, ela responde
 * menos que o mês.
 *
 * A janela vem por extenso (`09:00–11:00`) em vez da hora de início sozinha,
 * porque aqui ela é o conteúdo, não a etiqueta: é o que o líder compara quando
 * dois projetos disputam o mesmo salão. Bloco curto devolve os dois à mesma
 * linha, pelo CSS (`.fc-timegrid-event-short`).
 *
 * Os dois canais de cor são os mesmos do chip — matiz = situação, preenchimento
 * = acesso —, e é isso que faz a Semana e o Mês se lerem como a mesma grade.
 */
function TimeBlock({ arg }: { arg: EventContentArg }) {
  const item = arg.event.extendedProps.item as CalendarItem | undefined;
  if (!item) return null;

  const { start, end } = arg.event;
  const isFull = item.access === 'full';

  return (
    <div
      title={describeItem(item)}
      className={cn(
        'ibft-time-block flex h-full w-full flex-col gap-px overflow-hidden',
        'rounded-[4px] border-l-2 px-1.5 py-0.5',
        'transition-[filter,background-color] duration-fast ease-out hover:brightness-[0.97]',
        chipClassOf(item),
      )}
    >
      <span
        className={cn(
          'ibft-time-block-title min-w-0 break-words text-[0.6875rem] leading-tight',
          isFull ? 'font-medium text-foreground' : 'font-normal text-muted-foreground',
        )}
      >
        {arg.event.title}
      </span>
      {start ? (
        <span className="ibft-time-block-time tabular shrink-0 whitespace-nowrap text-[0.625rem] leading-tight text-muted-foreground">
          {formatTimeOfLocal(start)}
          {end ? `\u2013${formatTimeOfLocal(end)}` : ''}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A célula do dia: o número e, em 375px, até quatro marcadores (decisão 59).
 *
 * No celular a leitura real acontece na LISTA abaixo, que ali vira a tela
 * inteira — e como ela é sempre do dia selecionado, já é a anatomia certa para
 * telefone. O que a grade precisa responder num aparelho é só *em que dias tem
 * coisa, e de que tipo*, e um ponto por matiz responde isso sem espremer título
 * nenhum.
 */
function DayCell({
  dayNumber,
  marker,
}: {
  dayNumber: string;
  marker?: { dots: string[]; count: number };
}) {
  return (
    <div className="flex w-full flex-col items-end gap-1">
      {/* A classe é o que o disco de "hoje" pinta (ver `calendar.css`) — ela
          precisa envolver só o número, nunca os marcadores. */}
      <span className="ibft-day-number">{dayNumber}</span>
      {marker ? (
        <span className="flex items-center gap-0.5 pr-0.5">
          {marker.dots.map((dot) => (
            <span key={dot} aria-hidden="true" className={cn('size-1.5 rounded-full', dot)} />
          ))}
          <span className="tabular ml-0.5 text-[0.625rem] text-muted-foreground">
            {marker.count}
          </span>
        </span>
      ) : null}
    </div>
  );
}

/**
 * A hora de um `Date` que o FullCalendar devolveu — lida dos campos LOCAIS.
 *
 * Mesma razão de `localDayOf`: aquela data já é hora de parede da igreja, e
 * passá-la por `formatTime` (que converte para São Paulo) aplicaria o
 * deslocamento duas vezes.
 */
function formatTimeOfLocal(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/**
 * Os dias civis que um item cobre — o mesmo critério de `itemsOfDay`.
 *
 * O laço tem teto de 400 iterações como guarda contra data corrompida: sem ele,
 * um `ends_at` anterior ao `starts_at` (que nenhuma RPC aceita hoje, mas que um
 * `update` manual em produção produziria) travaria a renderização da grade.
 */
function daysCovered(item: CalendarItem): string[] {
  const days: string[] = [];
  let day = dayOf(item);
  const end = endOf(item);
  const last = end ? toDateInputValue(end) : day;

  for (let i = 0; day <= last && i < 400; i += 1) {
    days.push(day);
    day = addDaysToDateInput(day, 1);
  }
  return days;
}
